type TraceTags = {
  is_widget: boolean
}

interface TraceMetadata {
  /** Arbitrary data stored on a trace. */
  data?: Record<string, unknown>
  /** Indexed (ie searchable) tags associated with a trace. */
  tags?: Partial<TraceTags>
}

interface TraceCallbackOptions {
  /**
   * Traces the callback as a child of the active trace.
   * @param name - The name of the child.
   * @param callback - The callback to trace. The child trace will run for the duration of the callback.
   * @param metadata - Any data or tags to include in the child trace.
   */
  traceChild<T>(name: string, callback: TraceCallback<T>, metadata?: TraceMetadata): Promise<T>
  setTraceData(key: string, value: unknown): void
  setTraceTag<K extends keyof TraceTags>(key: K, value: TraceTags[K]): void
  /**
   * Sets the status of a trace. If unset, the status will be set to 'ok' (or 'internal_error' if the callback throws).
   * @param status - If a number is passed, the corresponding http status will be used.
   */
  setTraceStatus(status: number | string): void
  /** Sets the error data of a trace. If unset and the callback throws, the thrown error will be set. */
  setTraceError(error: unknown): void
}
type TraceCallback<T> = (options: TraceCallbackOptions) => Promise<T>

/**
 * Creates no-op trace callback options that can be reused.
 */
function createNoOpOptions(): TraceCallbackOptions {
  return {
    traceChild: async <T>(name: string, callback: TraceCallback<T>, metadata?: TraceMetadata): Promise<T> => {
      // Recursively create no-op options for nested traces
      return callback(createNoOpOptions())
    },
    setTraceData: () => {},
    setTraceTag: () => {},
    setTraceStatus: () => {},
    setTraceError: () => {},
  }
}

/**
 * Traces the callback, adding any metadata to the trace.
 * This is now a no-op implementation that just executes the callback.
 * @param name - The name of your trace.
 * @param callback - The callback to trace. The trace will run for the duration of the callback.
 * @param metadata - Any data or tags to include in the trace.
 */
export async function trace<T>(name: string, callback: TraceCallback<T>, metadata?: TraceMetadata): Promise<T> {
  // No-op implementation - just execute the callback
  return callback(createNoOpOptions())
}
